import { Injectable } from "@angular/core";

/**
 * Recovers from stale lazy-chunk load failures that happen after a new deploy.
 *
 * The app is built with content-hashed chunk filenames (`chunk-XXXX.js`). When a
 * user keeps a tab open across a deploy, their in-memory `index.html` still points
 * at the old chunk hashes, which no longer exist on the server. Navigating to a
 * lazy route then rejects the dynamic `import()` with "Failed to fetch dynamically
 * imported module", surfacing as a Router `NavigationError`.
 *
 * `index.html` is served `no-cache`, so a full page reload always fetches the fresh
 * manifest with the new chunk hashes — a single reload self-heals the navigation.
 * A sessionStorage timestamp guard prevents an infinite reload loop when a chunk is
 * missing for a genuine reason rather than a stale deploy.
 */
@Injectable({ providedIn: "root" })
export class ChunkLoadRecoveryService {
  private static readonly RELOAD_KEY = "auvious.chunk-reload";
  private static readonly RELOAD_WINDOW_MS = 15000;

  private readonly chunkErrorPattern =
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk .* failed/i;

  isChunkLoadError(error: unknown): boolean {
    const message =
      error instanceof Error
        ? `${error.name} ${error.message}`
        : String((error as { message?: string })?.message ?? error ?? "");

    return this.chunkErrorPattern.test(message);
  }

  /**
   * Reloads the page once if `error` is a stale-chunk load failure. Returns whether
   * a reload was triggered.
   */
  reloadIfChunkError(error: unknown): boolean {
    if (!this.isChunkLoadError(error)) {
      return false;
    }

    const now = Date.now();
    const last = this.readTimestamp();

    // Guard against reload loops: if we already reloaded recently and still hit a
    // chunk error, the chunk is likely missing for a real reason — stop reloading.
    if (last && now - last < ChunkLoadRecoveryService.RELOAD_WINDOW_MS) {
      return false;
    }

    this.writeTimestamp(now);
    window.location.reload();
    return true;
  }

  /** Clears the reload guard after a successful navigation so a later deploy can self-heal again. */
  clearReloadFlag(): void {
    try {
      sessionStorage.removeItem(ChunkLoadRecoveryService.RELOAD_KEY);
    } catch {
      // sessionStorage may be unavailable (private mode) — nothing to clear.
    }
  }

  private readTimestamp(): number {
    try {
      return Number(
        sessionStorage.getItem(ChunkLoadRecoveryService.RELOAD_KEY) ?? 0
      );
    } catch {
      return 0;
    }
  }

  private writeTimestamp(value: number): void {
    try {
      sessionStorage.setItem(
        ChunkLoadRecoveryService.RELOAD_KEY,
        String(value)
      );
    } catch {
      // sessionStorage may be unavailable (private mode) — reload will still fire
      // once; without persistence the loop guard is best-effort.
    }
  }
}
